import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  getTicketParaAutofactura,
  autofacturarTicket,
  descargarPDFAutofactura,
  descargarXMLAutofactura,
  reenviarEmailAutofactura,
  getRegimenesFiscalesPublic,
  getUsosCFDIPublic
} from '../services/api';
import {
  FileText,
  CheckCircle,
  AlertCircle,
  Loader2,
  Download,
  Receipt,
  Store,
  ShoppingBag,
  Mail
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

const Autofacturacion = () => {
  const { ticketId } = useParams();
  const [loading, setLoading] = useState(true);
  const [ticket, setTicket] = useState(null);
  const [error, setError] = useState(null);
  const [facturaGenerada, setFacturaGenerada] = useState(null);
  const [facturaExistente, setFacturaExistente] = useState(null);
  const [generandoFactura, setGenerandoFactura] = useState(false);
  
  // Catalogs
  const [regimenes, setRegimenes] = useState([]);
  const [usosCFDI, setUsosCFDI] = useState([]);
  
  // Form
  const [formData, setFormData] = useState({
    rfc: '',
    nombre: '',
    regimen_fiscal: '601',
    uso_cfdi: 'G03',
    domicilio_fiscal: '',
    email: ''
  });

  useEffect(() => {
    loadTicket();
    loadCatalogs();
  }, [ticketId]);

  const loadTicket = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getTicketParaAutofactura(ticketId);
      if (response.data.ya_facturado) {
        setFacturaExistente(response.data);
      } else {
        setTicket(response.data);
      }
    } catch (err) {
      console.error('Error loading ticket:', err);
      setError(err.response?.data?.detail || 'Ticket no encontrado o no disponible para facturar');
    } finally {
      setLoading(false);
    }
  };

  const loadCatalogs = async () => {
    try {
      const [regRes, usosRes] = await Promise.all([
        getRegimenesFiscalesPublic(),
        getUsosCFDIPublic()
      ]);
      setRegimenes(regRes.data);
      setUsosCFDI(usosRes.data);
    } catch (err) {
      console.error('Error loading catalogs:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGenerandoFactura(true);
    try {
      const response = await autofacturarTicket(ticketId, {
        rfc: formData.rfc.toUpperCase(),
        nombre: formData.nombre,
        regimen_fiscal: formData.regimen_fiscal,
        uso_cfdi: formData.uso_cfdi,
        domicilio_fiscal: formData.domicilio_fiscal,
        email: formData.email || null
      });
      setFacturaGenerada(response.data.factura);
      toast.success('¡Factura generada exitosamente!');
    } catch (err) {
      console.error('Error generating invoice:', err);
      toast.error(err.response?.data?.detail || 'Error al generar la factura');
    } finally {
      setGenerandoFactura(false);
    }
  };

  const handleDescargarPDF = async () => {
    try {
      const response = await descargarPDFAutofactura(facturaGenerada.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `factura_${facturaGenerada.uuid}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF descargado');
    } catch (err) {
      toast.error('Error al descargar PDF');
    }
  };

  const handleDescargarXML = async () => {
    try {
      const response = await descargarXMLAutofactura(facturaGenerada.id);
      const blob = new Blob([response.data], { type: 'application/xml' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `factura_${facturaGenerada.uuid}.xml`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('XML descargado');
    } catch (err) {
      toast.error('Error al descargar XML');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-teal-600 animate-spin mx-auto" />
          <p className="mt-4 text-slate-600">Cargando información del ticket...</p>
        </div>
      </div>
    );
  }

  // Already invoiced state
  if (facturaExistente) {
    return <FacturaExistenteView data={facturaExistente} formatCurrency={formatCurrency} />;
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">Ticket No Disponible</h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <Link 
            to="/"
            className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            Ir al Inicio
          </Link>
        </div>
      </div>
    );
  }

  // Success state - Factura generada
  if (facturaGenerada) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-slate-100 flex items-center justify-center p-4">
        <Toaster position="top-center" richColors />
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">¡Factura Generada!</h1>
          <p className="text-slate-600 mb-6">Tu factura ha sido timbrada correctamente</p>
          
          <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-slate-500 text-sm">Ticket:</span>
                <span className="font-mono font-medium">{facturaGenerada.ticket_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500 text-sm">Total:</span>
                <span className="font-bold text-teal-600">{formatCurrency(facturaGenerada.total)}</span>
              </div>
              <div className="pt-2 border-t border-slate-200">
                <span className="text-slate-500 text-xs block mb-1">UUID (Folio Fiscal):</span>
                <span className="font-mono text-xs text-slate-700 break-all">{facturaGenerada.uuid}</span>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleDescargarPDF}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors"
            >
              <Download className="w-5 h-5" />
              Descargar PDF
            </button>
            <button
              onClick={handleDescargarXML}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
            >
              <FileText className="w-5 h-5" />
              Descargar XML
            </button>
          </div>
          
          <p className="mt-6 text-sm text-slate-500">
            {formData.email && `También hemos enviado la factura a ${formData.email}`}
          </p>
        </div>
      </div>
    );
  }

  // Form state
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4">
      <Toaster position="top-center" richColors />
      
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-100 rounded-2xl mb-4">
            <Receipt className="w-8 h-8 text-teal-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Facturación de tu Compra</h1>
          <p className="text-slate-600 mt-2">Completa tus datos fiscales para generar tu factura</p>
        </div>

        {/* Ticket Info */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-start gap-4 mb-4">
            <div className="p-3 bg-slate-100 rounded-xl">
              <Store className="w-6 h-6 text-slate-600" />
            </div>
            <div className="flex-1">
              <h2 className="font-bold text-slate-900">{ticket.negocio.nombre}</h2>
              <p className="text-sm text-slate-500">RFC: {ticket.negocio.rfc}</p>
              {ticket.sucursal && <p className="text-sm text-slate-500">Sucursal: {ticket.sucursal}</p>}
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-500">Ticket</p>
              <p className="font-mono font-bold text-slate-900">{ticket.ticket_number}</p>
            </div>
          </div>

          <div className="border-t border-slate-100 pt-4">
            <div className="flex items-center gap-2 mb-3">
              <ShoppingBag className="w-4 h-4 text-slate-400" />
              <span className="text-sm font-medium text-slate-700">Productos</span>
            </div>
            <div className="space-y-2">
              {ticket.items.map((item, index) => (
                <div key={index} className="flex justify-between text-sm">
                  <span className="text-slate-600">
                    {item.cantidad}x {item.descripcion}
                  </span>
                  <span className="font-medium">{formatCurrency(item.subtotal)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-slate-100 mt-3 pt-3 space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Subtotal</span>
                <span>{formatCurrency(ticket.subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">IVA</span>
                <span>{formatCurrency(ticket.iva)}</span>
              </div>
              <div className="flex justify-between text-lg font-bold">
                <span>Total</span>
                <span className="text-teal-600">{formatCurrency(ticket.total)}</span>
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-3">
              Fecha: {formatDate(ticket.fecha)}
            </p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="font-bold text-slate-900 mb-4">Tus Datos Fiscales</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">RFC *</label>
              <input
                type="text"
                value={formData.rfc}
                onChange={(e) => setFormData({ ...formData, rfc: e.target.value.toUpperCase() })}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent font-mono uppercase"
                placeholder="XAXX010101000"
                maxLength={13}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Nombre o Razón Social *</label>
              <input
                type="text"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value.toUpperCase() })}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent uppercase"
                placeholder="Tu nombre completo"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Régimen Fiscal *</label>
              <select
                value={formData.regimen_fiscal}
                onChange={(e) => setFormData({ ...formData, regimen_fiscal: e.target.value })}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                required
              >
                {regimenes.length > 0 ? regimenes.map(r => (
                  <option key={r.Value} value={r.Value}>{r.Value} - {r.Name}</option>
                )) : (
                  <>
                    <option value="601">601 - General de Ley Personas Morales</option>
                    <option value="603">603 - Personas Morales con Fines no Lucrativos</option>
                    <option value="605">605 - Sueldos y Salarios</option>
                    <option value="606">606 - Arrendamiento</option>
                    <option value="612">612 - Personas Físicas con Actividades Empresariales</option>
                    <option value="621">621 - Incorporación Fiscal</option>
                    <option value="625">625 - Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas</option>
                    <option value="626">626 - Régimen Simplificado de Confianza</option>
                  </>
                )}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Uso del CFDI *</label>
              <select
                value={formData.uso_cfdi}
                onChange={(e) => setFormData({ ...formData, uso_cfdi: e.target.value })}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                required
              >
                {usosCFDI.length > 0 ? usosCFDI.map(u => (
                  <option key={u.Value} value={u.Value}>{u.Value} - {u.Name}</option>
                )) : (
                  <>
                    <option value="G01">G01 - Adquisición de mercancías</option>
                    <option value="G03">G03 - Gastos en general</option>
                    <option value="P01">P01 - Por definir</option>
                    <option value="S01">S01 - Sin efectos fiscales</option>
                  </>
                )}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Código Postal Fiscal *</label>
              <input
                type="text"
                value={formData.domicilio_fiscal}
                onChange={(e) => setFormData({ ...formData, domicilio_fiscal: e.target.value.replace(/\D/g, '') })}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="00000"
                maxLength={5}
                pattern="[0-9]{5}"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email (opcional)</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="tu@email.com"
              />
              <p className="text-xs text-slate-400 mt-1">Te enviaremos la factura a este correo</p>
            </div>
          </div>

          <button
            type="submit"
            disabled={generandoFactura}
            className="w-full mt-6 flex items-center justify-center gap-2 px-6 py-4 bg-teal-600 text-white text-lg font-medium rounded-xl hover:bg-teal-700 transition-colors disabled:opacity-50"
          >
            {generandoFactura ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Generando Factura...
              </>
            ) : (
              <>
                <FileText className="w-5 h-5" />
                Generar Mi Factura
              </>
            )}
          </button>
        </form>

        <p className="text-center text-xs text-slate-400 mt-6">
          Powered by Todo en Local - Facturación Electrónica CFDI 4.0
        </p>
      </div>
    </div>
  );
};

const FacturaExistenteView = ({ data, formatCurrency }) => {
  const factura = data.factura;
  const negocio = data.negocio;
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [downloadingXML, setDownloadingXML] = useState(false);
  const [email, setEmail] = useState('');
  const [enviando, setEnviando] = useState(false);

  const handleDescargarPDF = async () => {
    setDownloadingPDF(true);
    try {
      const response = await descargarPDFAutofactura(factura.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `factura_${factura.serie || ''}${factura.folio || ''}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF descargado');
    } catch (err) {
      toast.error('Error al descargar PDF');
    } finally {
      setDownloadingPDF(false);
    }
  };

  const handleDescargarXML = async () => {
    setDownloadingXML(true);
    try {
      const response = await descargarXMLAutofactura(factura.id);
      const blob = new Blob([response.data], { type: 'application/xml' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `factura_${factura.serie || ''}${factura.folio || ''}.xml`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('XML descargado');
    } catch (err) {
      toast.error('Error al descargar XML');
    } finally {
      setDownloadingXML(false);
    }
  };

  const handleEnviarEmail = async (e) => {
    e.preventDefault();
    if (!email) return;
    setEnviando(true);
    try {
      await reenviarEmailAutofactura(factura.id, { email });
      toast.success(`Factura enviada a ${email}`);
      setEmail('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar factura');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-slate-100 flex items-center justify-center p-4">
      <Toaster position="top-center" richColors />
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full">
        <div className="text-center mb-6">
          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-10 h-10 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Factura Ya Generada</h1>
          <p className="text-slate-600 mt-1">Este ticket ya fue facturado. Puedes descargar tu factura aquí.</p>
        </div>

        {/* Invoice info */}
        <div className="bg-slate-50 rounded-xl p-4 mb-6 space-y-2">
          {negocio?.nombre && (
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Negocio:</span>
              <span className="font-medium text-slate-900">{negocio.nombre}</span>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">Ticket:</span>
            <span className="font-mono font-medium">{factura.ticket_number}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">Receptor:</span>
            <span className="font-medium">{factura.receptor_nombre}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">RFC:</span>
            <span className="font-mono">{factura.receptor_rfc}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">Total:</span>
            <span className="font-bold text-teal-600">{formatCurrency(factura.total)}</span>
          </div>
          <div className="pt-2 border-t border-slate-200">
            <span className="text-slate-500 text-xs block mb-1">UUID (Folio Fiscal):</span>
            <span className="font-mono text-xs text-slate-700 break-all">{factura.uuid}</span>
          </div>
        </div>

        {/* Download buttons */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <button
            onClick={handleDescargarPDF}
            disabled={downloadingPDF}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors disabled:opacity-50"
          >
            {downloadingPDF ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
            {downloadingPDF ? 'Generando...' : 'Descargar PDF'}
          </button>
          <button
            onClick={handleDescargarXML}
            disabled={downloadingXML}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors disabled:opacity-50"
          >
            {downloadingXML ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileText className="w-5 h-5" />}
            {downloadingXML ? 'Descargando...' : 'Descargar XML'}
          </button>
        </div>

        {/* Resend email */}
        <form onSubmit={handleEnviarEmail} className="border-t border-slate-200 pt-4">
          <label className="block text-sm font-medium text-slate-700 mb-2">Enviar factura por correo</label>
          <div className="flex gap-2">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@email.com"
              required
              className="flex-1 px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
            <button
              type="submit"
              disabled={!email || enviando}
              className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              Enviar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Autofacturacion;
